import { Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { ShortlistProvider } from "@/contexts/ShortlistContext";
import { SupportProvider } from "@/contexts/SupportContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { SupportRoot } from "@/components/support/SupportRoot";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useLocation } from "react-router-dom";
import { resolveTenantHost } from "@/lib/tenant/host";
import { TenantProvider } from "@/contexts/TenantContext";

// Per-route ErrorBoundary that resets when the pathname changes,
// so navigating away from a broken page recovers the app automatically.
function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname} scope="route">{children}</ErrorBoundary>;
}

// Legacy model URLs must never render their own profile shell.
// They immediately collapse to the only public model detail route.
/** Redirect that keeps ?tab= and other query params so old links land on the right screen. */
function KeepQueryRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function TalentRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/profile/${id}${location.search}${location.hash}`} replace />;
}
// FocusAIChatbot is gesture-loaded via FocusAIChatbotLauncher (perf: avoids
// downloading the ~78 KB markdown/AI chunk on every cold pageview).
import FocusAIChatbotLauncher from "@/components/FocusAIChatbotLauncher";
import AdminRoute from "@/components/admin/AdminRoute";
import AgencyRoute from "@/components/agency-backend/AgencyRoute";
import ReplicaRoute from "@/components/replica/ReplicaRoute";
import SuspendedClientGate from "@/components/SuspendedClientGate";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ImportProgressDock } from "@/components/agency/ImportProgressDock";

import CommandPalette from "@/components/CommandPalette";

// Landing page loaded eagerly (critical path)
import Landing from "./pages/Landing";

// All other pages lazy-loaded with retry for code splitting
const AgentLandingV2 = lazyWithRetry(() => import("./pages/AgentLandingV2"));
const Developers = lazyWithRetry(() => import("./pages/Developers"));
const EmbedRoster = lazyWithRetry(() => import("./pages/embed/EmbedRoster"));
const EmbedPackages = lazyWithRetry(() => import("./pages/embed/EmbedPackages"));
const EmbedModel = lazyWithRetry(() => import("./pages/embed/EmbedModel"));
const PublicLayout = lazyWithRetry(() => import("./layouts/PublicLayout"));
const FocusLayout = lazyWithRetry(() => import("./layouts/FocusLayout"));
const AdminOverview = lazyWithRetry(() => import("./pages/admin/AdminOverview"));
const AdminApprovals = lazyWithRetry(() => import("./pages/admin/AdminApprovals"));
const AdminTrustReview = lazyWithRetry(() => import("./pages/admin/AdminTrustReview"));
const AdminBookings = lazyWithRetry(() => import("./pages/admin/AdminBookings"));
const AdminAgencies = lazyWithRetry(() => import("./pages/admin/AdminAgencies"));
const AdminClients = lazyWithRetry(() => import("./pages/admin/AdminClients"));
const AdminPayments = lazyWithRetry(() => import("./pages/admin/AdminPayments"));
const AdminEmails = lazyWithRetry(() => import("./pages/admin/AdminEmails"));
const AdminEmailHealth = lazyWithRetry(() => import("./pages/admin/AdminEmailHealth"));
const AdminEmailDesigns = lazyWithRetry(() => import("./pages/admin/AdminEmailDesigns"));
const AdminAiUsage = lazyWithRetry(() => import("./pages/admin/AdminAiUsage"));
const AdminAiTagAudits = lazyWithRetry(() => import("./pages/admin/AdminAiTagAudits"));
const AdminBriefFunnel = lazyWithRetry(() => import("./pages/admin/AdminBriefFunnel"));
const AdminOutcomes = lazyWithRetry(() => import("./pages/admin/AdminOutcomes"));
const AdminTimelineHealth = lazyWithRetry(() => import("./pages/admin/AdminTimelineHealth"));
const AdminAuditLog = lazyWithRetry(() => import("./pages/admin/AdminAuditLog"));
const AdminClientErrors = lazyWithRetry(() => import("./pages/admin/AdminClientErrors"));
const AdminDisputes = lazyWithRetry(() => import("./pages/admin/AdminDisputes"));
const AdminDisputeDetail = lazyWithRetry(() => import("./pages/admin/AdminDisputeDetail"));
const AdminClientDisputeDetail = lazyWithRetry(() => import("./pages/admin/AdminClientDisputeDetail"));
const AdminRefunds = lazyWithRetry(() => import("./pages/admin/AdminRefunds"));
const AdminRefundDetail = lazyWithRetry(() => import("./pages/admin/AdminRefundDetail"));
const AdminDisasterRecovery = lazyWithRetry(() => import("./pages/admin/AdminDisasterRecovery"));
const AdminNewsletters = lazyWithRetry(() => import("./pages/admin/AdminNewsletters"));
const AdminOutreachLists = lazyWithRetry(() => import("./pages/admin/AdminOutreachLists"));
const AdminLandingFeatured = lazyWithRetry(() => import("./pages/admin/AdminLandingFeatured"));
const AdminNewsletterEditor = lazyWithRetry(() => import("./pages/admin/AdminNewsletterEditor"));
const Index = lazyWithRetry(() => import("./pages/Index"));
const AgencyCastingForm = lazyWithRetry(() => import("./pages/AgencyCastingForm"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));

const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const AgencySignup = lazyWithRetry(() => import("./pages/AgencySignup"));
const AcceptAgencyInvite = lazyWithRetry(() => import("./pages/AcceptAgencyInvite"));
const AgencyOnboarding = lazyWithRetry(() => import("./pages/AgencyOnboarding"));
const AgencyDashboard = lazyWithRetry(() => import("./pages/AgencyDashboard"));
const AgencyCampaigns = lazyWithRetry(() => import("./pages/AgencyCampaigns"));
const AgencyCampaignDetail = lazyWithRetry(() => import("./pages/AgencyCampaignDetail"));
const AgencyCalendar = lazyWithRetry(() => import("./pages/AgencyCalendar"));
const AgencyPaymentSettings = lazyWithRetry(() => import("./pages/AgencyPaymentSettings"));
const AgencyModelEdit = lazyWithRetry(() => import("./pages/AgencyModelEdit"));
const AgencyContracts = lazyWithRetry(() => import("./pages/AgencyContracts"));
const AgencyContractEditor = lazyWithRetry(() => import("./pages/AgencyContractEditor"));
const AgencyAllBookings = lazyWithRetry(() => import("./pages/AgencyAllBookings"));
const AgencyBookingRequests = lazyWithRetry(() => import("./pages/AgencyBookingRequests"));
const AgencyBookingsContracts = lazyWithRetry(() => import("./pages/AgencyBookingsContracts"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const BookingPaymentSuccess = lazyWithRetry(() => import("./pages/BookingPaymentSuccess"));
const PayBookingBalance = lazyWithRetry(() => import("./pages/PayBookingBalance"));
const BrowseTalent = lazyWithRetry(() => import("./pages/BrowseTalent"));
const ModelProfilePage = lazyWithRetry(() => import("./pages/ModelProfilePage"));
const HandleResolver = lazyWithRetry(() => import("./pages/HandleResolver"));
const LandingWow = lazyWithRetry(() => import("./pages/LandingWow"));

const OpenCastingForm = lazyWithRetry(() => import("./pages/OpenCastingForm"));
const CastingPage = lazyWithRetry(() => import("./pages/CastingPage"));
const AgencyProfilePage = lazyWithRetry(() => import("./pages/AgencyProfilePage"));
const AgencySubmitPage = lazyWithRetry(() => import("./pages/AgencySubmitPage"));
const AgenciesAccepting = lazyWithRetry(() => import("./pages/AgenciesAccepting"));
const BriefBuilder = lazyWithRetry(() => import("./pages/brief/BriefBuilder"));
const PublicBrief = lazyWithRetry(() => import("./pages/brief/PublicBrief"));
const StudioList = lazyWithRetry(() => import("./pages/lab/studio/StudioList"));
const StudioEditor = lazyWithRetry(() => import("./pages/lab/studio/StudioEditor"));
const StudioShare = lazyWithRetry(() => import("./pages/lab/studio/StudioShare"));
const BriefStart = lazyWithRetry(() => import("./pages/brief-builder/BriefStart"));
const BriefView = lazyWithRetry(() => import("./pages/brief-builder/BriefView"));
const BriefPublicView = lazyWithRetry(() => import("./pages/brief-builder/BriefPublicView"));
const CastingBriefEditor = lazyWithRetry(() => import("./pages/brief-builder/CastingBriefEditor"));
const BriefSharedEdit = lazyWithRetry(() => import("./pages/brief-builder/BriefSharedEdit"));
const AgencyBriefs = lazyWithRetry(() => import("./pages/agency-backend/AgencyBriefs"));
const AgencyBriefDetail = lazyWithRetry(() => import("./pages/agency-backend/AgencyBriefDetail"));
const UsageBasics = lazyWithRetry(() => import("./pages/UsageBasics"));
const PackageDetail = lazyWithRetry(() => import("./pages/PackageDetail"));
const JoinPackage = lazyWithRetry(() => import("./pages/JoinPackage"));
const PublicPackageView = lazyWithRetry(() => import("./pages/PublicPackageView"));
const AgencyImportModels = lazyWithRetry(() => import("./pages/AgencyImportModels"));

const AgencyTalent = lazyWithRetry(() => import("./pages/agency-backend/AgencyTalent"));
const About = lazyWithRetry(() => import("./pages/About"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const AboutAgency = lazyWithRetry(() => import("./pages/AboutAgency"));
const RequestAgency = lazyWithRetry(() => import("./pages/RequestAgency"));
const CastingGuide = lazyWithRetry(() => import("./pages/CastingGuide"));
const AgencyDirectory = lazyWithRetry(() => import("./pages/AgencyDirectory"));
const MarketHub = lazyWithRetry(() => import("./pages/MarketHub"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ShortLinkRedirect = lazyWithRetry(() => import("./pages/ShortLinkRedirect"));
const AccessLinkRedeem = lazyWithRetry(() => import("./pages/AccessLinkRedeem"));
const ClientDashboard = lazyWithRetry(() => import("./pages/ClientDashboard"));

const GuidePackages = lazyWithRetry(() => import("./pages/GuidePackages"));
const GuideFittings = lazyWithRetry(() => import("./pages/GuideFittings"));
const GuideBookings = lazyWithRetry(() => import("./pages/GuideBookings"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const ConnectSocial = lazyWithRetry(() => import("./pages/ConnectSocial"));
const TikTokCallback = lazyWithRetry(() => import("./pages/TikTokCallback"));
const BookingRequestPage = lazyWithRetry(() => import("./pages/BookingRequestPage"));
const TestShootRequestPage = lazyWithRetry(() => import("./pages/TestShootRequestPage"));

// Agency Backend Pages
const AgencyRedirect = lazyWithRetry(() => import("./pages/agency-backend/AgencyRedirect"));
const AgencyDashboardOverview = lazyWithRetry(() => import("./pages/agency-backend/AgencyDashboardOverview"));
const PortfolioSyncReview = lazyWithRetry(() => import("./pages/agency-backend/PortfolioSyncReview"));
const RosterMergeReview = lazyWithRetry(() => import("./pages/agency-backend/RosterMergeReview"));

const AgencyTags = lazyWithRetry(() => import("./pages/agency-backend/AgencyTags"));
const AgencyRequests = lazyWithRetry(() => import("./pages/agency-backend/AgencyRequests"));
const AgencyPackages = lazyWithRetry(() => import("./pages/agency-backend/AgencyPackages"));
const AgencySendouts = lazyWithRetry(() => import("./pages/agency-backend/AgencySendouts"));
const AgencySendoutEditor = lazyWithRetry(() => import("./pages/agency-backend/AgencySendoutEditor"));
const AgencyPackageNew = lazyWithRetry(() => import("./pages/agency-backend/AgencyPackageNew"));
const AgencyTeam = lazyWithRetry(() => import("./pages/agency-backend/AgencyTeam"));
const AgencySettings = lazyWithRetry(() => import("./pages/agency-backend/AgencySettings"));
const AgencyWebsite = lazyWithRetry(() => import("./pages/agency-backend/AgencyWebsite"));
// AgencyModelDetail is deprecated - /agency/models/:id now renders AgencyModelEdit directly.
const AgencySubmissions = lazyWithRetry(() => import("./pages/agency-backend/AgencySubmissions"));
const AgencyAnalytics = lazyWithRetry(() => import("./pages/agency-backend/AgencyAnalytics"));
const AgencyInbox = lazyWithRetry(() => import("./pages/agency-backend/AgencyInbox"));
const AgencyCampaignReviews = lazyWithRetry(() => import("./pages/agency-backend/AgencyCampaignReviews"));
const AgencyCampaignReviewEditor = lazyWithRetry(() => import("./pages/agency-backend/AgencyCampaignReviewEditor"));
const PublicCampaignReview = lazyWithRetry(() => import("./pages/PublicCampaignReview"));
const AgencyCalendarPage = lazyWithRetry(() => import("./pages/agency-backend/AgencyCalendarPage"));
const AgencyCallsheetDayOf = lazyWithRetry(() => import("./pages/agency-backend/AgencyCallsheetDayOf"));
const AgencyClients = lazyWithRetry(() => import("./pages/agency-backend/AgencyClients"));
const AgencyUsageRights = lazyWithRetry(() => import("./pages/agency-backend/AgencyUsageRights"));
const AgencyGuestInquiries = lazyWithRetry(() => import("./pages/agency-backend/AgencyGuestInquiries"));
const AgencyInvoices = lazyWithRetry(() => import("./pages/agency-backend/AgencyInvoices"));
const AgencyInvoiceBuilder = lazyWithRetry(() => import("./pages/agency-backend/AgencyInvoiceBuilder"));
const AgencyInvoiceDetail = lazyWithRetry(() => import("./pages/agency-backend/AgencyInvoiceDetail"));
const AgencyCustomers = lazyWithRetry(() => import("./pages/agency-backend/AgencyCustomers"));
const AgencyCustomerDetail = lazyWithRetry(() => import("./pages/agency-backend/AgencyCustomerDetail"));
const AgencyAging = lazyWithRetry(() => import("./pages/agency-backend/AgencyAging"));
const AgencyAccounting = lazyWithRetry(() => import("./pages/agency-backend/AgencyAccounting"));
const AgencyModelStatements = lazyWithRetry(() => import("./pages/agency-backend/AgencyModelStatements"));
const AgencyModelStatementDetail = lazyWithRetry(() => import("./pages/agency-backend/AgencyModelStatementDetail"));
const PublicInvoice = lazyWithRetry(() => import("./pages/PublicInvoice"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const ClientUsageRights = lazyWithRetry(() => import("./pages/ClientUsageRights"));
// Digital Replica Licensing (feature flagged + beta-tester gated)
const ClientReplicaLicensing = lazyWithRetry(() => import("./pages/ClientReplicaLicensing"));
const ReplicaGenerate = lazyWithRetry(() => import("./pages/ReplicaGenerate"));
const StudioFreeform = lazyWithRetry(() => import("./pages/StudioFreeform"));
const ReplicaConsent = lazyWithRetry(() => import("./pages/ReplicaConsent"));
const LegacyReplicaRedirect = () => { const { licenseId } = useParams(); return <Navigate to={`/studio/${licenseId}`} replace />; };
const CloseAccount = lazyWithRetry(() => import("./pages/CloseAccount"));
const SiteHandleRoot = lazyWithRetry(() => import("./pages/agency-site/SiteHandleRoot"));
const TenantSite = lazyWithRetry(() => import("./pages/agency-site/TenantSite"));

// P2 Performance: sane global defaults. Realtime surfaces (notifications, inbox,
// booking events) should override per-query with shorter staleTime and/or
// refetchOnWindowFocus: true where freshness matters.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminAuthProvider>
          <ShortlistProvider>
            <FavoritesProvider>
            <SupportProvider>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <TenantProvider>
              <ScrollToTop />
              <ImportProgressDock />

              <Suspense fallback={<div className="flex min-h-dvh items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
              <RouteBoundary>
              {(() => { const m = resolveTenantHost().mode; return m === "subdomain" || m === "custom-domain"; })() ? (
                <TenantSite />
              ) : (
              <SuspendedClientGate>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/welcome" element={<LandingWow />} />
                {/* White-label agency websites: focuscasting.com/sites/:handle/* */}
                <Route path="/sites/:handle/*" element={<SiteHandleRoot />} />
                {/* Public handle URLs: focuscasting.com/@maya - resolves to model or agency */}
                <Route path="/:handle" element={<HandleResolver />} />
                <Route path="/s/:code" element={<ShortLinkRedirect />} />
                <Route path="/access/:token" element={<AccessLinkRedeem />} />
                <Route element={<PublicLayout />}>
                  <Route path="/about" element={<About />} />
                  <Route path="/about-agency" element={<AboutAgency />} />
                  <Route path="/request-agency" element={<RequestAgency />} />
                  <Route path="/casting-guide" element={<CastingGuide />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/usage-basics" element={<UsageBasics />} />
                  <Route path="/p/:token" element={<PublicPackageView />} />
                  <Route path="/directory" element={<AgencyDirectory />} />
                  <Route path="/models/:city" element={<MarketHub />} />
                  <Route path="/guide/packages" element={<GuidePackages />} />
                  <Route path="/guide/fittings" element={<GuideFittings />} />
                  <Route path="/guide/bookings" element={<GuideBookings />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/for-agencies" element={<AgentLandingV2 />} />
                  <Route path="/developers" element={<Developers />} />
                </Route>
                {/* Iframe embeds for agency websites - no chrome, no layout */}
                <Route path="/embed/:agencyHandle/roster" element={<EmbedRoster />} />
                <Route path="/embed/:agencyHandle/packages" element={<EmbedPackages />} />
                <Route path="/embed/:agencyHandle/model/:modelId" element={<EmbedModel />} />
                <Route path="/for-agencies-v2" element={<Navigate to="/for-agencies" replace />} />
                <Route path="/for-agencies-legacy" element={<Navigate to="/for-agencies" replace />} />
                
                <Route path="/agency-casting" element={<AgencyCastingForm />} />
                <Route path="/open-casting" element={<OpenCastingForm />} />
                <Route path="/casting/:slug" element={<CastingPage />} />
                <Route path="/connect-social/:modelId" element={<ConnectSocial />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<Auth />} />
                <Route path="/auth/tiktok/callback" element={<TikTokCallback />} />
                <Route path="/dashboard" element={<ClientDashboard />} />
                
                <Route path="/client-dashboard" element={<KeepQueryRedirect to="/dashboard" />} />
                <Route path="/project/:id" element={<ProjectDetail />} />
                
                {/* Admin Dashboard Routes */}
                <Route path="/admin" element={<AdminRoute><AdminOverview /></AdminRoute>} />
                <Route path="/admin/approvals" element={<AdminRoute><AdminApprovals /></AdminRoute>} />
                <Route path="/admin/trust-review" element={<AdminRoute><AdminTrustReview /></AdminRoute>} />
                <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
                <Route path="/admin/agencies" element={<AdminRoute><AdminAgencies /></AdminRoute>} />
                <Route path="/admin/clients" element={<AdminRoute><AdminClients /></AdminRoute>} />
                <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
                <Route path="/admin/disputes" element={<AdminRoute><AdminDisputes /></AdminRoute>} />
                <Route path="/admin/disputes/:id" element={<AdminRoute><AdminDisputeDetail /></AdminRoute>} />
                <Route path="/admin/client-disputes/:id" element={<AdminRoute><AdminClientDisputeDetail /></AdminRoute>} />
                <Route path="/admin/refunds" element={<AdminRoute><AdminRefunds /></AdminRoute>} />
                <Route path="/admin/refunds/:id" element={<AdminRoute><AdminRefundDetail /></AdminRoute>} />
                <Route path="/admin/emails" element={<AdminRoute><AdminEmails /></AdminRoute>} />
                <Route path="/admin/email-health" element={<AdminRoute><AdminEmailHealth /></AdminRoute>} />
                <Route path="/admin/email-designs" element={<AdminRoute><AdminEmailDesigns /></AdminRoute>} />
                <Route path="/admin/ai-usage" element={<AdminRoute><AdminAiUsage /></AdminRoute>} />
                <Route path="/admin/ai-tag-audits" element={<AdminRoute><AdminAiTagAudits /></AdminRoute>} />
                <Route path="/admin/brief-funnel" element={<AdminRoute><AdminBriefFunnel /></AdminRoute>} />
                <Route path="/admin/outcomes" element={<AdminRoute><AdminOutcomes /></AdminRoute>} />
                <Route path="/admin/timeline-health" element={<AdminRoute><AdminTimelineHealth /></AdminRoute>} />
                <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
                <Route path="/admin/client-errors" element={<AdminRoute><AdminClientErrors /></AdminRoute>} />
                <Route path="/admin/newsletters" element={<AdminRoute><AdminNewsletters /></AdminRoute>} />
                <Route path="/admin/newsletters/outreach" element={<AdminRoute><AdminOutreachLists /></AdminRoute>} />
                <Route path="/admin/landing-talent" element={<AdminRoute><AdminLandingFeatured /></AdminRoute>} />
                <Route path="/admin/newsletters/:id" element={<AdminRoute><AdminNewsletterEditor /></AdminRoute>} />
                <Route path="/admin/disaster-recovery" element={<AdminRoute><AdminDisasterRecovery /></AdminRoute>} />

                {/* Private lab - hidden, admin only */}
                <Route path="/lab/studio" element={<AdminRoute><StudioList /></AdminRoute>} />
                <Route path="/lab/studio/:id" element={<AdminRoute><StudioEditor /></AdminRoute>} />
                <Route path="/lab/s/:slug" element={<StudioShare />} />

                
                {/* New Agency Backend Routes - Protected by AgencyRoute */}
                <Route path="/agency" element={<AgencyRoute><AgencyRedirect /></AgencyRoute>} />
                <Route path="/agency/signup" element={<AgencySignup />} />
                <Route path="/agency/accept-invite" element={<AcceptAgencyInvite />} />
                {/* /agency/onboarding migrated to FocusLayout below */}
                <Route path="/agency/dashboard" element={<AgencyRoute><AgencyDashboardOverview /></AgencyRoute>} />
                <Route path="/agency/portfolio-sync" element={<AgencyRoute><PortfolioSyncReview /></AgencyRoute>} />
                <Route path="/agency/models" element={<AgencyRoute><AgencyTalent /></AgencyRoute>} />
                <Route path="/agency/models/new" element={<AgencyRoute><AgencyModelEdit /></AgencyRoute>} />
                <Route path="/agency/models/:id" element={<AgencyRoute><AgencyModelEdit /></AgencyRoute>} />
                <Route path="/agency/models/:id/edit" element={<Navigate to="../" replace />} />
                <Route path="/agency/import" element={<AgencyRoute><AgencyImportModels /></AgencyRoute>} />
                <Route path="/agency/roster" element={<Navigate to="/agency/models?view=roster" replace />} />
                <Route path="/agency/roster/merge" element={<AgencyRoute><RosterMergeReview /></AgencyRoute>} />
                <Route path="/agency/submissions" element={<AgencyRoute><AgencySubmissions /></AgencyRoute>} />
                <Route path="/agency/tags" element={<AgencyRoute><AgencyTags /></AgencyRoute>} />
                <Route path="/agency/requests" element={<AgencyRoute><AgencyInbox /></AgencyRoute>} />
                <Route path="/agency/inbox" element={<AgencyRoute><AgencyInbox /></AgencyRoute>} />
                <Route path="/agency/campaign-reviews" element={<AgencyRoute><AgencyCampaignReviews /></AgencyRoute>} />
                <Route path="/agency/campaign-reviews/:id" element={<AgencyRoute><AgencyCampaignReviewEditor /></AgencyRoute>} />
                <Route path="/agency/casting-briefs" element={<Navigate to="/agency/requests" replace />} />
                <Route path="/agency/bookings" element={<Navigate to="/agency/requests" replace />} />
                <Route path="/agency/bookings/:id" element={<Navigate to="/agency/requests" replace />} />
                <Route path="/agency/packages" element={<AgencyRoute><AgencyPackages /></AgencyRoute>} />
                <Route path="/agency/packages/new" element={<AgencyRoute><AgencyPackageNew /></AgencyRoute>} />
                <Route path="/agency/sendouts" element={<AgencyRoute><AgencySendouts /></AgencyRoute>} />
                <Route path="/agency/sendouts/:id" element={<AgencyRoute><AgencySendoutEditor /></AgencyRoute>} />
                <Route path="/agency/settings/team" element={<AgencyRoute><AgencyTeam /></AgencyRoute>} />
                <Route path="/agency/team" element={<AgencyRoute><Navigate to="/agency/settings/team" replace /></AgencyRoute>} />
                <Route path="/agency/settings" element={<AgencyRoute><AgencySettings /></AgencyRoute>} />
                <Route path="/agency/website" element={<AgencyRoute><AgencyWebsite /></AgencyRoute>} />
                <Route path="/agency/calendar" element={<AgencyRoute><AgencyCalendarPage /></AgencyRoute>} />
                <Route path="/agency/callsheet/:id/day-of" element={<AgencyRoute><AgencyCallsheetDayOf /></AgencyRoute>} />
                <Route path="/agency/analytics" element={<AgencyRoute><Navigate to="/agency/finance/analytics" replace /></AgencyRoute>} />
                <Route path="/agency/clients" element={<AgencyRoute><AgencyClients /></AgencyRoute>} />
                <Route path="/agency/usage-rights" element={<AgencyRoute><AgencyUsageRights /></AgencyRoute>} />
                <Route path="/agency/guest-inquiries" element={<AgencyRoute><AgencyGuestInquiries /></AgencyRoute>} />
                <Route path="/agency/finance/invoices" element={<AgencyRoute><AgencyInvoices /></AgencyRoute>} />
                <Route path="/agency/finance/invoices/new" element={<AgencyRoute><AgencyInvoiceBuilder /></AgencyRoute>} />
                <Route path="/agency/finance/invoices/:id/edit" element={<AgencyRoute><AgencyInvoiceBuilder /></AgencyRoute>} />
                <Route path="/agency/finance/invoices/:id" element={<AgencyRoute><AgencyInvoiceDetail /></AgencyRoute>} />
                <Route path="/agency/finance/customers" element={<AgencyRoute><AgencyCustomers /></AgencyRoute>} />
                <Route path="/agency/finance/customers/:id" element={<AgencyRoute><AgencyCustomerDetail /></AgencyRoute>} />
                <Route path="/agency/finance/aging" element={<AgencyRoute><AgencyAging /></AgencyRoute>} />
                <Route path="/agency/finance/analytics" element={<AgencyRoute><AgencyAnalytics /></AgencyRoute>} />
                <Route path="/agency/finance" element={<Navigate to="/agency/finance/accounting" replace />} />
                <Route path="/agency/finance/accounting" element={<AgencyRoute><AgencyAccounting /></AgencyRoute>} />
                <Route path="/agency/finance/statements" element={<AgencyRoute><AgencyModelStatements /></AgencyRoute>} />
                <Route path="/agency/finance/statements/:id" element={<AgencyRoute><AgencyModelStatementDetail /></AgencyRoute>} />
                <Route path="/agency/contracts" element={<AgencyRoute><AgencyContracts /></AgencyRoute>} />
                <Route path="/agency/contracts/:id" element={<AgencyRoute><AgencyContractEditor /></AgencyRoute>} />
                <Route path="/invoice/:id" element={<PublicInvoice />} />
                <Route path="/review/:token" element={<PublicCampaignReview />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/replica/consent" element={<ReplicaConsent />} />
                <Route path="/usage-rights" element={<ClientUsageRights />} />
                <Route path="/studio" element={<ReplicaRoute><ClientReplicaLicensing /></ReplicaRoute>} />
                <Route path="/studio/create" element={<ReplicaRoute><StudioFreeform /></ReplicaRoute>} />
                <Route path="/studio/:licenseId" element={<ReplicaRoute><ReplicaGenerate /></ReplicaRoute>} />
                <Route path="/replicas" element={<Navigate to="/studio" replace />} />
                <Route path="/replicas/:licenseId" element={<LegacyReplicaRedirect />} />
                <Route path="/account/close" element={<CloseAccount />} />
                
                {/* Legacy Agency Routes - Redirect to new backend (also protected) */}
                <Route path="/profile" element={<Profile />} />
                <Route path="/agencies" element={<AgencyRoute><AgencyRedirect /></AgencyRoute>} />
                <Route path="/agencies/settings" element={<AgencyRoute><AgencySettings /></AgencyRoute>} />
                <Route path="/agencies/dashboard" element={<AgencyRoute><AgencyDashboardOverview /></AgencyRoute>} />
                <Route path="/agencies/campaigns" element={<Navigate to="/agency/campaigns" replace />} />
                <Route path="/agencies/campaigns/:id" element={<AgencyRoute><AgencyCampaignDetail /></AgencyRoute>} />
                <Route path="/agencies/campaigns/bookings/requests" element={<Navigate to="/agency/inbox" replace />} />
                <Route path="/agencies/campaigns/calendar" element={<Navigate to="/agency/calendar" replace />} />
                <Route path="/agencies/campaigns/bookings" element={<Navigate to="/agency/bookings" replace />} />
                <Route path="/agencies/campaigns/bookings/contracts" element={<Navigate to="/agency/contracts" replace />} />
                <Route path="/agencies/lists" element={<Navigate to="/agency/packages" replace />} />
                <Route path="/agencies/lists/:id" element={<Navigate to="/agency/packages" replace />} />
                <Route path="/agencies/payment-settings" element={<Navigate to="/agency/settings?tab=payment" replace />} />
                <Route path="/agencies/models/:id/edit" element={<AgencyRoute><AgencyModelEdit /></AgencyRoute>} />
                <Route path="/agencies/contracts" element={<Navigate to="/agency/contracts" replace />} />
                <Route path="/agencies/contracts/:id" element={<Navigate to="/agency/contracts" replace />} />
                <Route path="/agencies/import" element={<AgencyRoute><AgencyImportModels /></AgencyRoute>} />
                <Route path="/agencies/team" element={<AgencyRoute><Navigate to="/agency/settings/team" replace /></AgencyRoute>} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                {/* Distraction-free flows share FocusLayout (logo-only chrome) */}
                <Route element={<FocusLayout />}>
                  <Route path="/agency/onboarding" element={<AgencyRoute><AgencyOnboarding /></AgencyRoute>} />
                  <Route path="/booking-payment-success" element={<BookingPaymentSuccess />} />
                  <Route path="/booking/:bookingId/pay-balance" element={<PayBookingBalance />} />
                  <Route path="/book/:modelId" element={<BookingRequestPage />} />
                  <Route path="/book" element={<BookingRequestPage />} />
                </Route>
                <Route path="/browse" element={<BrowseTalent />} />
                <Route path="/test-shoot/:modelId" element={<TestShootRequestPage />} />
                <Route path="/profile/:id" element={<ModelProfilePage />} />
                <Route path="/model/:id" element={<TalentRedirect />} />
                <Route path="/talent/:id" element={<TalentRedirect />} />
                <Route path="/list/:slug" element={<Navigate to="/browse" replace />} />
                <Route path="/agency/:id" element={<AgencyProfilePage />} />
                <Route path="/agency/:id/apply" element={<AgencySubmitPage />} />
                <Route path="/agencies/apply" element={<AgenciesAccepting />} />
                <Route path="/briefs" element={<BriefStart />} />
                <Route path="/brief/new" element={<Navigate to="/brief-builder" replace />} />
                <Route path="/brief/:id" element={<BriefBuilder />} />
                <Route path="/legacy-b/:slug" element={<PublicBrief />} />
                <Route path="/brief-builder" element={<BriefStart />} />
                <Route path="/brief-builder/casting/:id" element={<CastingBriefEditor />} />
                <Route path="/brief-builder/:id" element={<BriefView />} />
                <Route path="/brief-builder/:id/view" element={<BriefView />} />
                <Route path="/brief-builder/shared/:slug" element={<BriefSharedEdit />} />
                <Route path="/b/:slug" element={<BriefPublicView />} />
                <Route path="/agency/briefs" element={<AgencyRoute><AgencyBriefs /></AgencyRoute>} />
                <Route path="/agency/briefs/:id" element={<AgencyRoute><AgencyBriefDetail /></AgencyRoute>} />
                <Route path="/package/:id" element={<PackageDetail />} />
                <Route path="/join-package/:token" element={<JoinPackage />} />
                <Route path="/my-profile" element={<Navigate to="/dashboard" replace />} />
                <Route path="/my-packages" element={<Navigate to="/dashboard?tab=packages" replace />} />
                <Route path="/my-castings" element={<Navigate to="/dashboard?tab=inbox" replace />} />
                <Route path="/my-bookings" element={<Navigate to="/dashboard?tab=inbox" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </SuspendedClientGate>
              )}
              </RouteBoundary>
              </Suspense>
              <ChatbotLauncherGate />
              <SupportRoot />
              <CommandPalette />
              </TenantProvider>
            </BrowserRouter>
            </TooltipProvider>
            </SupportProvider>
            </FavoritesProvider>
          </ShortlistProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

/** Hides the Focus AI launcher on tenant sites, /sites/* handles, and all
 *  /admin pages. Uses useLocation so it reacts to client-side navigation
 *  (a plain window.location check only evaluates on the first render). */
const ChatbotLauncherGate = () => {
  const { pathname } = useLocation();
  const mode = resolveTenantHost().mode;
  const isTenant = mode === "subdomain" || mode === "custom-domain";
  const isSiteHandle = pathname.startsWith("/sites/");
  const isAdmin = pathname.startsWith("/admin");
  if (isTenant || isSiteHandle || isAdmin) return null;
  return <FocusAIChatbotLauncher />;
};

export default App;